import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { currentUserShowSuccess, fetchCurrentUser } from '../../ducks/user.duck';
import { updateUserProfile } from '../../extensions/profile-slug/utils/api';
import { createImageVariantConfig } from '../../util/sdkLoader';

export const BANNER_VARIANT = 'banner';

// The shape `publicData.profileBanner` is stored in, and that ProviderProfileHeader
// and ProviderSettingsForm read back — a raw uuid plus the image's `attributes`,
// not the SDK image itself.
const toStoredBanner = (uploadedImage) => ({
  id: uploadedImage.id?.uuid ?? uploadedImage.id,
  attributes: uploadedImage.attributes ?? {},
});

// ================ Action types ================ //

export const CLEAR_UPDATED_FORM = 'app/ProfileSettingsPage/CLEAR_UPDATED_FORM';

export const UPLOAD_IMAGE_REQUEST = 'app/ProfileSettingsPage/UPLOAD_IMAGE_REQUEST';
export const UPLOAD_IMAGE_SUCCESS = 'app/ProfileSettingsPage/UPLOAD_IMAGE_SUCCESS';
export const UPLOAD_IMAGE_ERROR = 'app/ProfileSettingsPage/UPLOAD_IMAGE_ERROR';

export const UPLOAD_BANNER_REQUEST = 'app/ProfileSettingsPage/UPLOAD_BANNER_REQUEST';
export const UPLOAD_BANNER_SUCCESS = 'app/ProfileSettingsPage/UPLOAD_BANNER_SUCCESS';
export const UPLOAD_BANNER_ERROR = 'app/ProfileSettingsPage/UPLOAD_BANNER_ERROR';

export const UPDATE_PROFILE_REQUEST = 'app/ProfileSettingsPage/UPDATE_PROFILE_REQUEST';
export const UPDATE_PROFILE_SUCCESS = 'app/ProfileSettingsPage/UPDATE_PROFILE_SUCCESS';
export const UPDATE_PROFILE_ERROR = 'app/ProfileSettingsPage/UPDATE_PROFILE_ERROR';

// ================ Reducer ================ //

const initialState = {
  image: null,
  uploadImageError: null,
  uploadInProgress: false,
  updateInProgress: false,
  updateProfileError: null,
  banner: null,
  uploadBannerError: null,
  uploadBannerInProgress: false,
};

// The avatar saves itself as soon as it uploads (uploadImage dispatches
// updateProfile), but a banner stays pending until the user hits Save. Clearing
// both on every profile update wiped a banner the user had just picked (CH-498),
// so each is cleared only when this update actually carried it.
const clearSavedUploads = (params) => ({
  ...(params?.profileImageId ? { image: null } : {}),
  ...(params?.publicData && 'profileBanner' in params.publicData ? { banner: null } : {}),
});

export default function reducer(state = initialState, action = {}) {
  const { type, payload, params } = action;
  switch (type) {
    case UPLOAD_IMAGE_REQUEST:
      // payload.params: { id: 'tempId', file }
      return {
        ...state,
        image: { ...payload.params },
        uploadInProgress: true,
        uploadImageError: null,
      };
    case UPLOAD_IMAGE_SUCCESS: {
      // payload: { id: 'tempId', uploadedImage }
      const { id, uploadedImage } = payload;
      const { file } = state.image || {};
      const image = { id, imageId: uploadedImage.id, file, uploadedImage };
      return { ...state, image, uploadInProgress: false };
    }
    case UPLOAD_IMAGE_ERROR: {
      // eslint-disable-next-line no-console
      return { ...state, image: null, uploadInProgress: false, uploadImageError: payload.error };
    }

    case UPDATE_PROFILE_REQUEST:
      return {
        ...state,
        updateInProgress: true,
        updateProfileError: null,
      };
    case UPDATE_PROFILE_SUCCESS:
      return {
        ...state,
        ...clearSavedUploads(params),
        updateInProgress: false,
      };
    case UPDATE_PROFILE_ERROR:
      return {
        ...state,
        ...clearSavedUploads(params),
        updateInProgress: false,
        updateProfileError: payload,
      };

    case CLEAR_UPDATED_FORM:
      return {
        ...state,
        updateProfileError: null,
        uploadImageError: null,
        uploadBannerError: null,
      };

    case UPLOAD_BANNER_REQUEST:
      return {
        ...state,
        banner: { ...payload.params },
        uploadBannerInProgress: true,
        uploadBannerError: null,
      };
    case UPLOAD_BANNER_SUCCESS: {
      const { uploadedImage } = payload;
      const { file } = state.banner || {};
      const banner = { id: uploadedImage.id, file, uploadedImage };
      return { ...state, banner, uploadBannerInProgress: false };
    }
    case UPLOAD_BANNER_ERROR: {
      // eslint-disable-next-line no-console
      return {
        ...state,
        banner: null,
        uploadBannerInProgress: false,
        uploadBannerError: payload.error,
      };
    }
    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const clearUpdatedForm = () => ({
  type: CLEAR_UPDATED_FORM,
});

// SDK method: images.upload
export const uploadImageRequest = (params) => ({ type: UPLOAD_IMAGE_REQUEST, payload: { params } });
export const uploadImageSuccess = (result) => ({
  type: UPLOAD_IMAGE_SUCCESS,
  payload: result.data,
});
export const uploadImageError = (error) => ({
  type: UPLOAD_IMAGE_ERROR,
  payload: error,
  error: true,
});

// SDK method: sdk.currentUser.updateProfile
export const updateProfileRequest = (params) => ({
  type: UPDATE_PROFILE_REQUEST,
  payload: { params },
});
export const updateProfileSuccess = (result, params) => ({
  type: UPDATE_PROFILE_SUCCESS,
  payload: result.data,
  params,
});
export const updateProfileError = (error, params) => ({
  type: UPDATE_PROFILE_ERROR,
  payload: error,
  params,
  error: true,
});

export const uploadBannerRequest = (params) => ({
  type: UPLOAD_BANNER_REQUEST,
  payload: { params },
});
export const uploadBannerSuccess = (result) => ({
  type: UPLOAD_BANNER_SUCCESS,
  payload: result.data,
});
export const uploadBannerError = (error) => ({
  type: UPLOAD_BANNER_ERROR,
  payload: error,
  error: true,
});

// ================ Thunk ================ //

export const updateProfile = (actionPayload) => {
  return (dispatch, getState, sdk) => {
    dispatch(updateProfileRequest(actionPayload));

    const queryParams = {
      expand: true,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return updateUserProfile(actionPayload, queryParams)
      .then(async (response) => {
        dispatch(updateProfileSuccess(response, actionPayload));

        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        const currentUser = entities[0];

        // Update current user in state.user.currentUser through user.duck.js
        dispatch(currentUserShowSuccess(currentUser));

        const state = getState();
        const position =
          state.user.currentUser?.attributes?.profile?.publicData?.onboardingPosition;
        const hasProfileImage = !!response.data.data.relationships?.profileImage?.data;
        if (position === 'personalise-profile' && hasProfileImage) {
          await updateUserProfile({ publicData: { onboardingPosition: 'share-profile' } });
          window.posthog?.capture?.('onboarding transition share-profile');
          dispatch(fetchCurrentUser({ enforce: true }));
        }
      })
      .catch((e) => dispatch(updateProfileError(storableError(e), actionPayload)));
  };
};

// Images return imageId which we need to map with previously generated temporary id
export function uploadImage(actionPayload) {
  return (dispatch, getState, sdk) => {
    const id = actionPayload.id;
    dispatch(uploadImageRequest(actionPayload));

    const bodyParams = {
      image: actionPayload.file,
    };
    const queryParams = {
      expand: true,
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.images
      .upload(bodyParams, queryParams)
      .then((resp) => {
        const uploadedImage = resp.data.data;
        dispatch(uploadImageSuccess({ data: { id, uploadedImage } }));
        dispatch(updateProfile({ profileImageId: uploadedImage.id }));
      })
      .catch((e) => dispatch(uploadImageError({ id, error: storableError(e) })));
  };
}

export function uploadBanner(actionPayload) {
  return (dispatch, getState, sdk) => {
    const id = actionPayload.id;
    dispatch(uploadBannerRequest(actionPayload));

    const bodyParams = {
      image: actionPayload.file,
    };

    const queryParams = {
      expand: true,
      'fields.image': [`variants.${BANNER_VARIANT}`],
      ...createImageVariantConfig(BANNER_VARIANT, 1280, 9 / 16),
    };

    return sdk.images
      .upload(bodyParams, queryParams)
      .then((resp) => {
        const uploadedImage = resp.data.data;
        dispatch(uploadBannerSuccess({ data: { id, uploadedImage } }));
        dispatch(updateProfile({ publicData: { profileBanner: toStoredBanner(uploadedImage) } }));
      })
      .catch((e) => dispatch(uploadBannerError({ id, error: storableError(e) })));
  };
}

export const removeBanner = () => (dispatch) =>
  dispatch(updateProfile({ publicData: { profileBanner: null } }));
